// `autofocus`, the ATTRIBUTE — which is not what React's `autoFocus` prop does.
//
// ⚠️ THE TWO DIFFER IN EXACTLY THE WAY THIS PORT CANNOT AFFORD. React does not render the
// attribute: it calls `.focus()` on the node after mount. The HTML attribute, by contrast, is a
// PARSE-TIME instruction — the browser honours it for the first such element present when the
// document is parsed and does nothing for anything inserted later. Upstream's templates are all
// inserted later, by ui-router, so `autofocus` there is inert: production's login screen renders
// with NOTHING focused.
//
// Ours focused the email field, and Chrome draws a focus ring on a programmatically focused text
// input — 916 pixels at 200px wide, a black 2px border where production has a 1px grey one, and
// the whole of that screen's 0.51%. It reads as a border-colour defect and is a behaviour one.
//
// So: render the attribute, skip the focus. `useAutofocusAttribute()` returns a ref callback that
// sets it on mount — the DOM matches upstream's, and the screen matches production's.
//
// ⚠️ EXCEPT AT 1146, WHERE PRODUCTION DOES FOCUS IT, AND THAT IS A RACE RATHER THAN A RULE.
// Measured on production at all four widths: the email input carries the attribute and holds
// focus at 1146 and carries neither at 200, 378 and 700 — same markup, same single input, no
// width-dependent branch in the template. Chrome flushes autofocus candidates when an element is
// inserted, and whether the `ng-if` that inserts this one wins that race depends on how long the
// route takes to resolve. Reproducing it would mean reproducing a timing outcome; not
// reproducing it costs 2,136 pixels at one width and saves 3,076 across the other three.
import { useCallback } from 'react'

export function useAutofocusAttribute(): (node: HTMLElement | null) => void {
  return useCallback((node: HTMLElement | null) => {
    // Setting it after mount is deliberately too late to focus anything, which is the point:
    // it is the same no-op the attribute is upstream, with the same attribute in the DOM.
    node?.setAttribute('autofocus', '')
  }, [])
}
